/**
 * Edit ▸ Align and Edit ▸ Compare — two text files side by side.
 *
 * Both commands land here because here they are one computation shown two ways.
 * Align is the whole file with the differences marked in place; Compare is the
 * differences alone, with a line of context either side. A netlist diffed
 * against yesterday's is the reason either exists in a PCB program: it is how
 * you find the one connection that moved.
 *
 * That is not the split the original draws — see the note in `core/text/align`.
 * Its two are commands for driving a pair of side-by-side windows, one stepping
 * to the next difference and the other levelling the two views, and neither has
 * anything to work with in a program that shows one file at a time. What is
 * kept is the pair of questions they answer between them.
 *
 * The original takes the file in front and the next one open with no asking
 * (OGtxt1.cpp:1110). With more than two open, "the next one" is a guess, so
 * this asks which — seeded with the next, so with two open it is still one
 * keypress.
 */

import { alignFiles, identical, type AlignResult } from '@sircad/core/text/align.ts';
import { button, buttons, openDialog } from './dialog.ts';

export interface ComparableFile {
  id: string;
  title: string;
  text: string;
}

export interface CompareOptions {
  /** The file in front. */
  left: ComparableFile;
  /** Every other text file open, in tab order. */
  others: ComparableFile[];
  /** Compare shows only what differs; Align shows the whole file. */
  mode: 'align' | 'compare';
}

export function showCompareDialog(opts: CompareOptions): void {
  if (opts.others.length === 0) {
    openDialog({
      title: opts.mode === 'align' ? 'Edit Align' : 'Edit Compare',
      build(body, handle) {
        const p = document.createElement('p');
        p.className = 'modal-hint';
        p.textContent =
          `Nothing to ${opts.mode === 'align' ? 'align' : 'compare'} ${opts.left.title} `
          + 'against — only one text file is open. Open a second and try again.';
        body.append(p);
        button(buttons(body), 'Close', () => handle.close());
      },
    });
    return;
  }

  let right = opts.others[0]!;

  openDialog({
    title: opts.mode === 'align' ? 'Edit Align' : 'Edit Compare',
    // Two files side by side, each with its line numbers. Nothing about that
    // fits in a narrow column.
    wide: true,
    build(body, handle) {
      const bar = document.createElement('div');
      bar.className = 'cmp-bar';
      body.append(bar);

      const leftName = document.createElement('span');
      leftName.className = 'cmp-name';
      leftName.textContent = opts.left.title;

      const against = document.createElement('span');
      against.className = 'cmp-against';
      against.textContent = 'against';

      // Seeded with the next file open, which is what the original takes
      // without asking. With two files open this is the only choice there is.
      const pick = document.createElement('select');
      for (const o of opts.others) {
        const el = document.createElement('option');
        el.value = o.id;
        el.textContent = o.title;
        pick.append(el);
      }
      pick.value = right.id;
      pick.addEventListener('change', () => {
        right = opts.others.find((o) => o.id === pick.value) ?? right;
        run();
      });

      bar.append(leftName, against, pick);

      const summary = document.createElement('p');
      summary.className = 'modal-hint cmp-summary';
      body.append(summary);

      const table = document.createElement('div');
      table.className = 'cmp-table';
      body.append(table);

      function run(): void {
        table.replaceChildren();
        let result: AlignResult;
        try {
          result = alignFiles(opts.left.text, right.text);
        } catch (err) {
          summary.textContent = (err as Error).message;
          return;
        }

        if (identical(result)) {
          summary.textContent =
            `${opts.left.title} and ${right.title} are the same, all `
            + `${result.same} line${result.same === 1 ? '' : 's'} of them.`;
          return;
        }

        summary.textContent = [
          `${result.changed} changed`,
          `${result.added} added`,
          `${result.removed} removed`,
          `${result.same} unchanged`,
        ].join(' · ');

        // Compare shows the differences alone; Align shows the file with them
        // marked in place. One line of context either side of a difference,
        // because a changed line with nothing round it is unreadable.
        const wanted = opts.mode === 'align'
          ? result.rows.map((_, i) => i)
          : contextAround(result, 1);

        let last = -1;
        for (const i of wanted) {
          if (last >= 0 && i > last + 1) {
            const gap = document.createElement('div');
            gap.className = 'cmp-gap';
            gap.textContent = `⋯ ${i - last - 1} unchanged`;
            table.append(gap);
          }
          last = i;

          const r = result.rows[i]!;
          const row = document.createElement('div');
          row.className = `cmp-row ${r.kind}`;

          const ln = document.createElement('span');
          ln.className = 'cmp-no';
          ln.textContent = r.leftNo === null ? '' : String(r.leftNo);
          const lt = document.createElement('span');
          lt.className = 'cmp-text';
          lt.textContent = r.left ?? '';

          const rn = document.createElement('span');
          rn.className = 'cmp-no';
          rn.textContent = r.rightNo === null ? '' : String(r.rightNo);
          const rt = document.createElement('span');
          rt.className = 'cmp-text';
          rt.textContent = r.right ?? '';

          row.append(ln, lt, rn, rt);
          table.append(row);
        }
      }

      run();
      button(buttons(body), 'Close', () => handle.close());
    },
  });
}

/** The rows that differ, plus `n` unchanged either side of each run. */
function contextAround(result: AlignResult, n: number): number[] {
  const keep = new Set<number>();
  result.rows.forEach((r, i) => {
    if (r.kind === 'same') return;
    for (let k = Math.max(0, i - n); k <= Math.min(result.rows.length - 1, i + n); k++) {
      keep.add(k);
    }
  });
  return [...keep].sort((a, b) => a - b);
}
